// Reading and writing Excel files
import { audit } from './audit.js';
import { SUP_CATS } from './supplier-links.js';

// ── Project page: details, import from a spreadsheet, export, delete
let _excelP = null;
// The PDF library and its fonts and images (estimates, internal P&L; public/assets.js) are loaded the first time a
// PDF is needed, not with the app.
let _pdfP = null;
const script = src => new Promise((res, rej) => {
  const s = document.createElement('script');
  s.src = src; s.onload = res; s.onerror = rej;
  document.head.appendChild(s);
});
export function loadJsPDF() {
  if (window.jspdf && window.GB_ASSETS) return Promise.resolve(window.jspdf);
  if (!_pdfP) _pdfP = Promise.all([window.jspdf || script('./vendor/jspdf.umd.min.js'), window.GB_ASSETS || script('./assets.js')])
    .then(() => window.jspdf)
    .catch(() => { _pdfP = null; throw new Error('The PDF maker failed to load. Check the connection and try again.'); });
  return _pdfP;
}
export function loadExcelJS() {
  if (window.ExcelJS) return Promise.resolve(window.ExcelJS);
  if (!_excelP) _excelP = new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = './vendor/exceljs.min.js';
    s.onload = () => res(window.ExcelJS);
    s.onerror = () => { _excelP = null; rej(new Error('The Excel reader failed to load.')); };
    document.head.appendChild(s);
  });
  return _excelP;
}
export { sheetRows } from './xlsx/read.js';
// The budget as a Glassbox workbook: Summary, Budget (importable back) and Rates (Budget toolbar, Project settings).
// The workbook code and the logo artwork load only when someone exports.
export async function exportBudgetXlsx(project, user) {
  const [ExcelJS, { buildBudgetWorkbook }, kit] = await Promise.all([loadExcelJS(), import('./xlsx/budget-xlsx.js'), import('./xlsx/kit.js')]);
  const wb = buildBudgetWorkbook(ExcelJS, { project, user });
  audit('budget.xlsx', { project: project.name });
  saveFile(await kit.workbookBlob(wb), `${kit.fileSafe(project.name || 'Budget')} - Budget ${new Date().toISOString().slice(0, 10)}.xlsx`);
}
// One sub-budget as a Glassbox workbook (Sub-budgets page); it imports back on the same page
export async function exportSubBudgetXlsx(project, sb, fx, user) {
  const [ExcelJS, { buildSubBudgetWorkbook }, kit] = await Promise.all([loadExcelJS(), import('./xlsx/subbudget-xlsx.js'), import('./xlsx/kit.js')]);
  const wb = buildSubBudgetWorkbook(ExcelJS, { project, sb, fx, user });
  audit('subbudget.xlsx', { project: project.name, subBudget: sb.name });
  saveFile(await kit.workbookBlob(wb), `${kit.fileSafe(project.name || 'Project')} - ${kit.fileSafe(sb.name || 'Sub-budget')} ${new Date().toISOString().slice(0, 10)}.xlsx`);
}
// Reads a sub-budget back from an .xlsx: the first sheet with a "Main budget line" column, as applySubBudgetImport's result
export async function readSubBudgetXlsx(file, sb, sections, newId) {
  const [ExcelJS, { parseSubBudgetRows, applySubBudgetImport }, { sheetRows }] = await Promise.all([loadExcelJS(), import('./xlsx/subbudget-xlsx.js'), import('./xlsx/read.js')]);
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  for (const ws of wb.worksheets) {
    const parsed = parseSubBudgetRows(sheetRows(ws));
    if (parsed) return applySubBudgetImport(sb, parsed, sections, newId);
  }
  return { error: 'No sub-budget was found in that file. Use a file exported from the Sub-budgets page (or the budget export\'s Sub-budgets sheet).' };
}
function saveFile(blob, name) {
  if (window.__prismSave) return window.__prismSave(blob, name);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}
// The supplier database (or the part shown on the Suppliers page) as a Glassbox sheet with a filter on every column.
// about: what the list is ("All 75 suppliers", "12 of 75 suppliers · Lisbon")
const SUPPLIER_LOOK = { Supplier: { kind: 'strong' }, Category: { kind: 'tag', tags: SUP_CATS.map(c => c[1]) }, Email: { kind: 'email' }, Website: { kind: 'url' }, Rating: { kind: 'rating', al: 'center' },
  Preferred: { kind: 'yes', al: 'center' }, Description: { wrap: true }, 'Internal notes': { wrap: true }, 'In tenders': { al: 'center' }, Awarded: { al: 'center' },
  'Added by': { kind: 'muted' }, 'Last edited': { kind: 'muted' } };
export async function exportSuppliersXlsx(rows, columns, filename, about, user) {
  const [ExcelJS, { listSheet }, kit] = await Promise.all([loadExcelJS(), import('./xlsx/list-xlsx.js'), import('./xlsx/kit.js')]);
  const wb = kit.newWorkbook(ExcelJS, { user, title: 'Suppliers' });
  listSheet(wb, { name: 'Suppliers', kicker: 'Supplier database', title: 'Suppliers', sub: about || `${rows.length} suppliers`,
    note: `Exported ${kit.today()}${user?.name ? ' by ' + user.name : ''}  ·  contains contact details: keep it within the team`,
    columns: columns.map(([h, w]) => ({ h, w, ...(SUPPLIER_LOOK[h] || {}) })), rows, freezeX: 1 });
  audit('suppliers.xlsx', { rows: rows.length });
  saveFile(await kit.workbookBlob(wb), filename);
}
